import { Popover as PopoverPrimitive } from '@base-ui/react/popover';

import { usePortalContainer } from '../../config-context';
import { cn } from '../../lib/utils';

function Popover({ ...props }: PopoverPrimitive.Root.Props) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />;
}

function PopoverTrigger({ ...props }: PopoverPrimitive.Trigger.Props) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />;
}

/**
 * `anchor` and the collision props (`collisionBoundary` / `collisionPadding` /
 * `collisionAvoidance` / `positionMethod`) are passed straight to the Positioner
 * so a caller can pin the popup to something that is **not** its trigger — a
 * cursor point, or a rect measured elsewhere (`@floating-ui`'s virtual element
 * shape: an object with just `getBoundingClientRect`). Positioning itself,
 * including flipping and whether to fall back to the perpendicular axis, stays
 * the library's job (见 `ui-component-sourcing`: 浮层定位不得自造).
 */
function PopoverContent({
  className,
  side = 'bottom',
  sideOffset = 4,
  align = 'center',
  alignOffset = 0,
  anchor,
  collisionBoundary,
  collisionPadding,
  collisionAvoidance,
  positionMethod,
  container,
  ...props
}: PopoverPrimitive.Popup.Props &
  Pick<
    PopoverPrimitive.Positioner.Props,
    | 'align'
    | 'alignOffset'
    | 'anchor'
    | 'collisionAvoidance'
    | 'collisionBoundary'
    | 'collisionPadding'
    | 'positionMethod'
    | 'side'
    | 'sideOffset'
  > &
  Pick<PopoverPrimitive.Portal.Props, 'container'>) {
  const resolved = usePortalContainer(container);
  return (
    <PopoverPrimitive.Portal container={resolved}>
      <PopoverPrimitive.Positioner
        className="z-50 outline-none"
        side={side}
        sideOffset={sideOffset}
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        collisionBoundary={collisionBoundary}
        collisionPadding={collisionPadding}
        collisionAvoidance={collisionAvoidance}
        positionMethod={positionMethod}
      >
        <PopoverPrimitive.Popup
          data-slot="popover-content"
          className={cn(
            'z-50 w-72 origin-(--transform-origin) rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none transition-[opacity,transform] duration-150 ease-out data-starting-style:scale-95 data-starting-style:opacity-0 data-ending-style:scale-95 data-ending-style:opacity-0',
            className,
          )}
          {...props}
        />
      </PopoverPrimitive.Positioner>
    </PopoverPrimitive.Portal>
  );
}

function PopoverClose({ ...props }: PopoverPrimitive.Close.Props) {
  return <PopoverPrimitive.Close data-slot="popover-close" {...props} />;
}

export { Popover, PopoverClose, PopoverContent, PopoverTrigger };
